// ---------------------------------------------------------------------------
// SpeedyDock SDK — Drawer embed
// ---------------------------------------------------------------------------

import { SDK_PREFIX } from "./styles";
import type { DrawerOptions, EmbedInstance, EmbedEventType, EmbedEventHandler } from "./types";
import {
  buildEmbedUrl,
  resolveElement,
  createElement,
  createIframe,
  createSpinner,
  hideSpinner,
  EventEmitter,
  listenToIframe,
} from "./utils";

export function createDrawerEmbed(
  baseUrl: string,
  shortName: string,
  options: DrawerOptions = {},
): EmbedInstance {
  const emitter = new EventEmitter();
  const position = options.position ?? "right";
  const src = buildEmbedUrl(baseUrl, shortName, options);

  // Overlay
  const overlay = createElement("div", `${SDK_PREFIX}-overlay`);

  // Drawer
  const drawer = createElement(
    "div",
    `${SDK_PREFIX}-drawer ${position}`,
  );
  if (options.width) drawer.style.width = options.width;

  // Close button
  const closeBtn = createElement("button", `${SDK_PREFIX}-drawer-close`);
  closeBtn.innerHTML = "&#10005;";
  closeBtn.setAttribute("aria-label", "Close booking");
  drawer.appendChild(closeBtn);

  // Spinner (iframe deferred until first open)
  const spinner = createSpinner();
  drawer.appendChild(spinner);

  document.body.appendChild(overlay);
  document.body.appendChild(drawer);

  let iframe: HTMLIFrameElement | null = null;
  let unlisten: (() => void) | null = null;

  function ensureIframe(): HTMLIFrameElement {
    if (!iframe) {
      iframe = createIframe(src, "eager");
      drawer.appendChild(iframe);
      unlisten = listenToIframe(iframe, emitter, {
        onReady: () => hideSpinner(spinner),
        onClose: () => close(),
      });
    }
    return iframe;
  }

  function open(): void {
    ensureIframe();
    overlay.classList.add("open");
    drawer.classList.add("open");
    document.body.style.overflow = "hidden";
  }

  function close(): void {
    overlay.classList.remove("open");
    drawer.classList.remove("open");
    document.body.style.overflow = "";
  }

  // Wire trigger
  const trigger = resolveElement(options.trigger);
  const triggerHandler = () => open();
  if (trigger) {
    trigger.addEventListener("click", triggerHandler);
  }

  overlay.addEventListener("click", close);
  closeBtn.addEventListener("click", close);

  const escHandler = (e: KeyboardEvent) => {
    if (e.key === "Escape") close();
  };
  document.addEventListener("keydown", escHandler);

  return {
    destroy() {
      unlisten?.();
      trigger?.removeEventListener("click", triggerHandler);
      document.removeEventListener("keydown", escHandler);
      overlay.remove();
      drawer.remove();
      document.body.style.overflow = "";
    },
    on<T extends EmbedEventType>(event: T, handler: EmbedEventHandler<T>) {
      emitter.on(event, handler);
    },
    off<T extends EmbedEventType>(event: T, handler: EmbedEventHandler<T>) {
      emitter.off(event, handler);
    },
    navigate(path: string) {
      ensureIframe().contentWindow?.postMessage(
        { type: "speedydock:parent-navigate", data: { path } },
        "*",
      );
    },
  };
}
